"use client";

import {
  ArrowRight,
  Award,
  BookOpen,
  Flame,
  Shield,
  TrendingUp,
  Zap,
} from "lucide-react";
const GOALS = [
  { label: "Study hours (weekly)", current: 8, target: 12, unit: "hours", Icon: BookOpen, pct: 67 },
  { label: "Certification progress", current: 62, target: 100, unit: "%", Icon: TrendingUp, pct: 62 },
  { label: "Study streak", current: 12, target: 14, unit: "days", Icon: Flame, pct: 86 },
] as const;

/** Static miniature of /onboarding step 1 — student sets their certification. */
export function StudentOnboardingPreview() {
  return (
    <div
      className="absolute inset-0 overflow-hidden bg-gradient-to-br from-blue-50 via-white to-indigo-50 pointer-events-none select-none"
      aria-hidden="true"
    >
      <div className="absolute inset-0 flex items-center justify-center p-3 sm:p-4">
        <div className="w-full bg-white rounded-xl border border-gray-200 shadow-sm p-3">
          <div className="flex justify-between text-[9px] text-gray-400 mb-1">
            <span>Step 1 of 5</span>
            <span>0% complete</span>
          </div>
          <div className="h-1 rounded-full bg-gray-100 mb-2.5 overflow-hidden">
            <div className="h-full w-[8%] bg-blue-600 rounded-full" />
          </div>
          <div className="flex items-center gap-2 mb-2.5">
            <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600 shrink-0">
              <BookOpen className="w-3.5 h-3.5" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold text-gray-900 leading-tight">
                What are you studying for?
              </p>
              <p className="text-[9px] text-gray-500 leading-tight">
                We&apos;ll tailor your feed and recommendations.
              </p>
            </div>
          </div>
          <div className="relative">
            <Award className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-400" />
            <div className="h-8 rounded-md border border-blue-300 bg-white pl-8 pr-2 flex items-center text-[11px] text-gray-900">
              CIS
            </div>
          </div>
          <div className="mt-1 rounded-md border border-gray-200 bg-white overflow-hidden shadow-sm">
            <div className="px-2.5 py-1.5 text-[10px] flex items-center gap-1.5 bg-blue-50 text-gray-900">
              <Award className="h-2.5 w-2.5 text-blue-600 shrink-0" />
              CISSP
            </div>
            <div className="px-2.5 py-1.5 text-[10px] flex items-center gap-1.5 text-gray-700 border-t border-gray-100">
              <Award className="h-2.5 w-2.5 text-gray-400 shrink-0" />
              CISM
            </div>
          </div>
          <div className="flex items-center justify-between mt-2.5">
            <span className="text-[9px] text-gray-400">Set up later</span>
            <span className="inline-flex items-center gap-1 h-6 px-2.5 rounded-md bg-blue-600 text-white text-[9px] font-medium">
              Continue
              <ArrowRight className="h-2.5 w-2.5" />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Static 4:3 preview of the /student journey bar + goals tracker. */
export function StudentProgressTrackerPreview() {
  return (
    <div
      className="relative aspect-[4/3] w-full max-w-[360px] mx-auto bg-gray-100 overflow-hidden pointer-events-none select-none"
      aria-hidden="true"
    >
      <div className="absolute inset-0 p-3 sm:p-4 flex items-center justify-center">
        <div className="w-full max-w-[320px] space-y-2.5">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm px-3 py-2.5">
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center h-7 w-7 rounded-full bg-blue-50 border border-blue-200 shrink-0">
                <Shield className="h-3.5 w-3.5 text-blue-600" strokeWidth={2.5} />
              </div>
              <span className="text-[11px] font-semibold text-gray-900">CAPM</span>
              <div className="flex-1 h-[6px] rounded-full border border-gray-300 bg-gray-100 overflow-hidden">
                <div className="h-full w-[62%] bg-blue-600 rounded-full" />
              </div>
              <span className="text-[11px] font-semibold text-gray-700">62%</span>
            </div>
            <div className="flex items-center gap-1.5 mt-2">
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-orange-500 text-white text-[9px] font-semibold">
                1 month 17 days · Studying
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-gray-300 text-[9px] font-semibold text-orange-500 bg-white">
                <Flame className="h-2.5 w-2.5" />
                12-day streak
              </span>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-3">
            <p className="text-[11px] font-semibold text-gray-900 flex items-center gap-1.5 mb-2.5">
              <Zap className="h-3.5 w-3.5 text-blue-600" />
              My Goals
            </p>
            <div className="space-y-2.5">
              {GOALS.map((goal) => (
                <div key={goal.label}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <goal.Icon className="h-3 w-3 text-blue-600 shrink-0" />
                    <span className="text-[10px] font-medium text-gray-700 flex-1 truncate">
                      {goal.label}
                    </span>
                    <span className="text-[9px] text-gray-500 whitespace-nowrap">
                      {goal.current} / {goal.target} {goal.unit}
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                    <div
                      className="h-full bg-blue-600 rounded-full"
                      style={{ width: `${goal.pct}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const PMP_TIPS = [
  "Do 2 timed mock exams in the last 2 weeks.",
  "For situational questions, pick what a PM would do first.",
  "Review weak ITTO process groups — not the whole guide.",
] as const;

/** Static 4:3 preview of a PMP exam-tips forum thread. */
export function ForumThreadPreview() {
  return (
    <div
      className="relative aspect-[4/3] w-full max-w-[360px] mx-auto bg-gray-100 overflow-hidden pointer-events-none select-none"
      aria-hidden="true"
    >
      <div className="absolute inset-0 p-2.5 flex items-stretch justify-center">
        <div className="w-full max-w-[320px] bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden flex flex-col">
          <div className="h-1 bg-gradient-to-r from-blue-500 to-indigo-500" />

          <div className="px-3 pt-2 pb-1.5">
            <div className="flex items-center gap-1.5 mb-1">
              <span className="inline-flex items-center rounded-md bg-blue-50 text-blue-700 border border-blue-200 px-1.5 py-0.5 text-[9px] font-medium">
                PMP
              </span>
            </div>
            <p className="text-[12px] font-bold text-gray-900 leading-snug">
              PMP exam tips that actually worked
            </p>
          </div>

          <div className="px-3 pb-2.5">
            <div className="bg-gray-50 rounded-lg border border-gray-100 px-2.5 py-1.5 space-y-1">
              {PMP_TIPS.map((tip) => (
                <p key={tip} className="text-[10px] text-gray-700 leading-snug">
                  • {tip}
                </p>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
